import { LogOut } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ROL_LABEL } from '@/lib/roles';
import { useMe } from '@/hooks/use-me';
import { useVersion } from '@/hooks/use-version';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';

// Heurística de iniciales (igual RISIT): 1 palabra -> 1ª letra; 2 -> 1ª+2ª;
// 3+ -> 1ª de la primera + 1ª de la tercera (salta el segundo nombre/apellido).
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0]!.charAt(0).toUpperCase();
  if (partes.length === 2) return (partes[0]!.charAt(0) + partes[1]!.charAt(0)).toUpperCase();
  return (partes[0]!.charAt(0) + partes[2]!.charAt(0)).toUpperCase();
}

// Footer del sidebar (MVP §5.0): avatar con iniciales, nombre + rol,
// "Cerrar sesión". Lee la sesión del mismo cache ['auth','me'] que
// RequireAuth — no hay un context de auth separado en RedeApp.
// R4b (Ajuste 31): el disparador es un SidebarMenuButton `size="lg"` — mismo
// patrón que el AccountButton de c-sidebar-2 (esa combinación es la única de
// las ocho que anula el padding al colapsar, así el avatar llena el
// cuadrado de 32 px del rail sin recorte). El contenido del menú pasa a
// DropdownMenuLabel / Group / Separator TAL CUAL (c-dropdown-menu-9). Avatar
// sin pisar el `size` (prop del componente, no className).
export function UserMenu() {
  const { data: user } = useMe();
  const { data: version } = useVersion();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const logoutMutation = useMutation({
    mutationFn: () => api('/api/auth/logout', { method: 'POST' }),
    onSettled: () => {
      try {
        localStorage.removeItem('redeapp.auth.lastUser');
      } catch {
        // localStorage bloqueado: no es crítico
      }
      queryClient.setQueryData(['auth', 'me'], null);
      navigate('/login', { replace: true });
    },
  });

  if (!user) return null;

  const fotoUrl = `/api/usuarios/${user.id}/foto?v=${user.fotoVersion ?? 0}`;

  return (
    <DropdownMenu>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenuTrigger render={<SidebarMenuButton size="lg" />}>
            {/* Foto real de Microsoft (GET /api/usuarios/:id/foto). Base UI
                Avatar ya resuelve el fallback solo: AvatarImage no monta
                hasta que el status de carga es "loaded", así que un 404 deja
                ver el AvatarFallback de iniciales sin lógica propia de error
                acá. */}
            <Avatar>
              <AvatarImage src={fotoUrl} alt="" />
              <AvatarFallback>{iniciales(user.nombre)}</AvatarFallback>
            </Avatar>
            <span className="grid min-w-0 flex-1 text-left leading-tight group-data-[collapsible=icon]:sr-only">
              <span className="truncate text-sm font-medium">{user.nombre}</span>
              <span className="truncate text-xs text-sidebar-foreground/70 capitalize">{ROL_LABEL[user.rol]}</span>
            </span>
          </DropdownMenuTrigger>
        </SidebarMenuItem>
      </SidebarMenu>
      <DropdownMenuContent side="right" align="end" sideOffset={12} className="w-64">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-2 py-2">
            <Avatar size="lg">
              <AvatarImage src={fotoUrl} alt="" />
              <AvatarFallback>{iniciales(user.nombre)}</AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-foreground">{user.nombre}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* Versión visible — antes no había forma de saber si esta pestaña
              tenía el bundle nuevo tras un deploy (caso real: Juan no podía
              confirmar si ya tenía 0.10.0). Mientras /api/health no respondió
              (o falló) se muestra la versión del bundle (__APP_VERSION__,
              inyectada en build); apenas contesta, se usa la versión real del
              backend. El entorno solo se muestra fuera de producción — en
              prod no aporta y es un dato de infra de más. Si el bundle de esta
              pestaña quedó atrás del backend, se avisa para que recargue. */}
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            {version
              ? `RedeApp v${version.version}${version.env !== 'production' ? ` · ${version.env}` : ''}`
              : `RedeApp v${__APP_VERSION__}`}
            {version && __APP_VERSION__ !== version.version && (
              <div className="text-warning">
                Esta pestaña tiene una versión anterior (v{__APP_VERSION__}) — recargá la página
              </div>
            )}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => logoutMutation.mutate()}>
            <LogOut aria-hidden="true" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
